import { useCallback } from "react";
import { getCustomerList } from "src/apis/customerApi";
import { getEmployeeList } from "src/apis/employeeApi";
import { MST4060_R01DetailDto } from "../Dto_MST4060_R01";

interface UseMST4060_R01CellChangeProps {
	updateMST4060_R01DetailList: (index: number, data: MST4060_R01DetailDto) => void;
	settingCodeSearchModalProps: (
		category: string,
		search: string,
		isInfo: boolean,
		handleFoucsId: string,
		rowData: MST4060_R01DetailDto,
		valueIndex: number,
		originalData?: MST4060_R01DetailDto,
	) => void;
}

// 상세 셀 변경을 검증하고 거래처·등록자 코드 검색을 연결한다.
export function useMST4060_R01CellChange({
	updateMST4060_R01DetailList,
	settingCodeSearchModalProps,
}: UseMST4060_R01CellChangeProps) {
	// 거래처 코드 직접 입력 시 검증/검색
	const handleCustomCdSearch = useCallback(
		async (customCd: string, rowData: MST4060_R01DetailDto, oldValue?: string) => {
			if (!customCd?.trim()) {
				updateMST4060_R01DetailList(rowData.valueIndex, {
					...rowData,
					customCd: "",
					customNm: "",
					isModified: true,
				});
				return;
			}

			const originalData = {
				...rowData,
				customCd: oldValue || rowData.customCd,
				customNm: rowData.customNm,
			};

			try {
				const response = await getCustomerList(customCd);
				const foundCustomer = response.data.data.find((customer: any) => customer.customCd === customCd);

				if (foundCustomer) {
					updateMST4060_R01DetailList(rowData.valueIndex, {
						...rowData,
						customCd: foundCustomer.customCd || "",
						customNm: foundCustomer.customNm || "",
						isModified: true,
					});
				} else {
					settingCodeSearchModalProps(
						"거래처",
						customCd,
						false,
						"customCd",
						rowData,
						rowData.valueIndex,
						originalData,
					);
				}
			} catch (error) {
				console.error("거래처 검색 오류:", error);
				updateMST4060_R01DetailList(rowData.valueIndex, originalData);
				settingCodeSearchModalProps(
					"거래처",
					customCd || "all",
					false,
					"customCd",
					rowData,
					rowData.valueIndex,
					originalData,
				);
			}
		},
		[updateMST4060_R01DetailList, settingCodeSearchModalProps],
	);

	// 등록자 사번 직접 입력 시 사원을 검증하고, 일치하지 않으면 검색 팝업을 연다.
	const handleInspectEmpnoSearch = useCallback(
		async (inspectEmpno: string, rowData: MST4060_R01DetailDto, oldValue?: string) => {
			if (!inspectEmpno?.trim()) {
				updateMST4060_R01DetailList(rowData.valueIndex, {
					...rowData,
					inspectEmpno: "",
					inspectEmpNm: "",
					isModified: true,
				});
				return;
			}

			const originalData = {
				...rowData,
				inspectEmpno: oldValue || rowData.inspectEmpno,
				inspectEmpNm: rowData.inspectEmpNm,
			};

			try {
				const response = await getEmployeeList(inspectEmpno);
				const employee = response.data.data.find(value => value.empNo === inspectEmpno);

				if (employee) {
					updateMST4060_R01DetailList(rowData.valueIndex, {
						...rowData,
						inspectEmpno: employee.empNo || "",
						inspectEmpNm: employee.empNm || "",
						isModified: true,
					});
					return;
				}
			} catch (error) {
				console.error("등록자 검색 오류:", error);
			}

			updateMST4060_R01DetailList(rowData.valueIndex, originalData);
			settingCodeSearchModalProps(
				"등록자",
				inspectEmpno || "all",
				false,
				"inspectEmpno",
				rowData,
				rowData.valueIndex,
				originalData,
			);
		},
		[updateMST4060_R01DetailList, settingCodeSearchModalProps],
	);

	// 변경된 컬럼별 후속 검증을 수행하고 저장 대상임을 표시한다.
	const handleCellValueChanged = useCallback(
		(e: any) => {
			const rowData = e.data as MST4060_R01DetailDto;
			if (!rowData || !rowData.valueIndex) return;

			const { colDef, newValue, oldValue } = e;
			const field = colDef?.field as keyof MST4060_R01DetailDto;

			if (field === "customCd" && newValue !== oldValue) {
				handleCustomCdSearch(newValue, rowData, oldValue);
				return;
			}

			if (field === "inspectEmpno" && newValue !== oldValue) {
				handleInspectEmpnoSearch(newValue, rowData, oldValue);
				return;
			}

			updateMST4060_R01DetailList(rowData.valueIndex, {
				...rowData,
				isModified: true,
			});
		},
		[updateMST4060_R01DetailList, handleCustomCdSearch, handleInspectEmpnoSearch],
	);

	return { handleCellValueChanged };
}
